import React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';

import {
  SectionComponent,
  SectionLink,
  SectionFlattenProps,
} from '../section/Section.props';
import FooterAccordion from './FooterAccordion';
import * as Styled from './FooterLinkboxes.styled';
import { translate } from 'shared/utils/translate';

const getSize = count => 12 / count;

const getHeaderLink = links => {
  if (!links.length) return;
  return links[0];
};

const getChildLinks = links => {
  if (!links.length) return [];
  return links.slice(1);
};

const LinkComponent = ({
  link,
  color,
  intl,
}: WrappedComponentProps & {
  link: SectionLink;
  color?: string;
}) => {
  const translated = translate({ intl, id: link.title });
  if (!link.url) return <Styled.Text color={color}>{translated}</Styled.Text>;
  return (
    <Styled.Link href={link.url} color={color}>
      {translate({ intl, id: link.title })}
    </Styled.Link>
  );
};

const FooterLinkboxDesktop = ({
  columnSize,
  component,
  color,
  intl,
}: WrappedComponentProps & {
  columnSize: number;
  component: SectionComponent;
  color?: string;
}) => {
  const headerLink = getHeaderLink(component.links || []);
  if (!headerLink) return null;
  const childLinks = getChildLinks(component.links || []);
  return (
    <Styled.LinksList key={component.id}>
      <Styled.HeaderLink>
        <LinkComponent link={headerLink} color={color} intl={intl} />
      </Styled.HeaderLink>
      {childLinks.map((link, i) => (
        <Styled.FooterLink key={i}>
          <LinkComponent link={link} color={color} intl={intl} />
        </Styled.FooterLink>
      ))}
    </Styled.LinksList>
  );
};

const FooterLinkboxMobile = ({
  component,
  color,
  intl,
}: WrappedComponentProps & {
  component: SectionComponent;
  color?: string;
}) => {
  const headerLink = getHeaderLink(component);
  if (!headerLink) return null;
  const childLinks = getChildLinks(component);
  return (
    <FooterAccordion
      key={component.id}
      header={
        <Styled.HeaderLink>
          <LinkComponent link={headerLink} color={color} intl={intl} />
        </Styled.HeaderLink>
      }
    >
      {childLinks.map((link, i) => (
        <Styled.FooterLink key={i}>
          <LinkComponent link={link} color={color} intl={intl} />
        </Styled.FooterLink>
      ))}
    </FooterAccordion>
  );
};

const FooterLinkboxes = ({
  components,
  theme,
  intl,
}: WrappedComponentProps & SectionFlattenProps) => {
  if (!components || !components.length) return null;
  const columnSize = getSize(components.length);

  const color = theme?.color || '';
  const backgroundColor = theme?.backgroundColor || '';

  return (
    <>
      <Styled.MobileContent backgroundColor={backgroundColor}>
        <Styled.LinksList>
          {components.map(component => (
            <FooterLinkboxMobile
              key={component.id}
              component={component}
              color={color}
              intl={intl}
            />
          ))}
        </Styled.LinksList>
      </Styled.MobileContent>
      <Styled.DesktopContent backgroundColor={backgroundColor}>
        <Styled.FlexContainer>
          {components.map(component => (
            <FooterLinkboxDesktop
              key={component.id}
              component={component}
              columnSize={columnSize}
              color={color}
              intl={intl}
            />
          ))}
        </Styled.FlexContainer>
      </Styled.DesktopContent>
    </>
  );
};

export default injectIntl(FooterLinkboxes);
